// ═══════════════════════════════════════════════════════════
// CRM PAGE v2 - TXMark COMPLETO
// Archivo: src/pages/CRMPage.jsx
// Tabs: Dashboard | Contactos | Pipeline | Actividades | Equipo | WhatsApp
// ═══════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import crmAPI from '../api/crmAPI';

// ── Helpers ───────────────────────────────────────────────
const fmtCurrency = (v, cur = 'COP') =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(v || 0);
const SCORE_COLOR = (s) => s >= 76 ? '#ef4444' : s >= 51 ? '#f59e0b' : s >= 26 ? '#3b82f6' : '#6b7280';
const SCORE_LABEL = (c) => ({ urgente: '🔥 Urgente', caliente: '♨️ Caliente', tibio: '🌤 Tibio', frio: '🧊 Frío' }[c] || c);
const TYPE_ICON = { call: '📞', whatsapp: '💬', meeting: '🤝', email: '📧', note: '📝' };
const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'var(--font-body)' };
const labelStyle = { fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '5px' };
const cardStyle = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' };
const STATUSES = [
  { v: 'lead', l: 'Lead', c: '#3b82f6' },
  { v: 'qualified', l: 'Calificado', c: '#f59e0b' },
  { v: 'client', l: 'Cliente', c: '#10b981' },
  { v: 'lost', l: 'Perdido', c: '#ef4444' },
];

// ════════════════════════════════════════════════════════════
// SETUP WIZARD
// ════════════════════════════════════════════════════════════
function SetupWizard({ onDone }) {
  const [form, setForm] = useState({ name: '', slug: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error('El nombre es requerido');
    if (!form.slug.trim()) return toast.error('El identificador es requerido');
    setSaving(true);
    try {
      await crmAPI.setupTenant(form);
      toast.success('¡Empresa creada! Bienvenido al CRM');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al crear empresa'); }
    setSaving(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ ...cardStyle, maxWidth: '460px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '8px' }}>🏢</div>
          <div style={{ fontSize: '20px', fontWeight: '800' }}>Configura tu empresa</div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Crea tu espacio de trabajo en el CRM</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={labelStyle}>Nombre de la empresa</label>
            <input style={inputStyle} value={form.name} onChange={e => {
              const name = e.target.value;
              setForm(f => ({ ...f, name, slug: name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }));
            }} placeholder="Ej: Mi Empresa SAS" />
          </div>
          <div>
            <label style={labelStyle}>Identificador único</label>
            <input style={inputStyle} value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} placeholder="mi-empresa" />
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Solo letras minúsculas, números y guiones</div>
          </div>
          <button disabled={saving} onClick={handleSubmit} style={{ padding: '12px', borderRadius: '8px', border: 'none', background: saving ? '#7c3aed90' : '#7c3aed', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginTop: '8px' }}>
            {saving ? 'Creando...' : '🚀 Crear mi empresa'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// DASHBOARD TAB
// ════════════════════════════════════════════════════════════
function DashboardTab() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    crmAPI.getDashboard().then(r => setMetrics(r.data)).catch(() => setMetrics(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>Cargando métricas...</div>;
  if (!metrics) return <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>Sin datos disponibles o permisos insuficientes.</div>;

  const kpis = [
    { label: 'Contactos', value: metrics.total_contacts, icon: '👥', color: '#3b82f6' },
    { label: 'Deals activos', value: metrics.total_deals, icon: '💼', color: '#8b5cf6' },
    { label: 'Valor pipeline', value: fmtCurrency(metrics.total_value), icon: '💰', color: '#10b981' },
    { label: 'Conversión', value: `${metrics.conversion_rate}%`, icon: '📈', color: '#f59e0b' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
        {kpis.map(k => (
          <div key={k.label} style={{ ...cardStyle, borderLeft: `3px solid ${k.color}` }}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>{k.icon}</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: k.color }}>{k.value}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{k.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div style={cardStyle}>
          <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px' }}>📊 Deals por etapa</div>
          {metrics.deals_by_stage.map(s => (
            <div key={s.stage} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.color }} />
                <span style={{ fontSize: '13px' }}>{s.stage}</span>
              </div>
              <div style={{ fontSize: '13px', fontWeight: '600' }}>{s.count} <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>({fmtCurrency(s.value)})</span></div>
            </div>
          ))}
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px' }}>🎯 Por fuente</div>
          {metrics.contacts_by_source.filter(s => s.count > 0).map(s => (
            <div key={s.source} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.color }} />
                <span style={{ fontSize: '13px' }}>{s.source}</span>
              </div>
              <span style={{ fontSize: '13px', fontWeight: '600' }}>{s.count}</span>
            </div>
          ))}
        </div>
      </div>
      {metrics.recent_activities?.length > 0 && (
        <div style={cardStyle}>
          <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px' }}>⚡ Actividad reciente</div>
          {metrics.recent_activities.map(a => (
            <div key={a.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: '16px' }}>{TYPE_ICON[a.type] || '📝'}</span>
              <div>
                <div style={{ fontSize: '13px' }}>{a.title}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{new Date(a.created_at + 'Z').toLocaleString('es-CO')}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// CONTACT DETAIL MODAL
// ════════════════════════════════════════════════════════════
function ContactDetail({ contact, sources, pipeline, onClose, onEdit, onRefresh }) {
  const [activities, setActivities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [score, setScore] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [loadingBriefing, setLoadingBriefing] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showDealForm, setShowDealForm] = useState(false);
  const [activityText, setActivityText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [activityForm, setActivityForm] = useState({ type: 'note', title: '', body: '', direction: 'outbound' });
  const [dealForm, setDealForm] = useState({ title: '', value: 0, stage_id: '', currency: 'COP' });

  useEffect(() => {
    crmAPI.listActivities({ contact_id: contact.id }).then(r => setActivities(r.data)).catch(() => {});
    crmAPI.listDeals({ contact_id: contact.id }).then(r => setDeals(r.data)).catch(() => {});
  }, [contact.id]);

  const handleScore = async () => {
    try {
      const r = await crmAPI.scoreContact(contact.id);
      setScore(r.data);
      toast.success('Score generado por ARIA ✓');
    } catch { toast.error('Error generando score'); }
  };

  const handleBriefing = async () => {
    setLoadingBriefing(true);
    try {
      const r = await crmAPI.getBriefing(contact.id);
      setBriefing(r.data.briefing);
    } catch { toast.error('Error generando briefing'); }
    setLoadingBriefing(false);
  };

  const handleExtract = async () => {
    if (!activityText.trim()) return toast.error('Escribe la actividad');
    setExtracting(true);
    try {
      await crmAPI.extractActivity(activityText);
      toast.success('Actividad registrada por IA ✓');
      setActivityText('');
      const r = await crmAPI.listActivities({ contact_id: contact.id });
      setActivities(r.data);
    } catch { toast.error('Error procesando'); }
    setExtracting(false);
  };

  const handleCreateActivity = async () => {
    try {
      await crmAPI.createActivity({ ...activityForm, contact_id: contact.id });
      toast.success('Actividad creada ✓');
      setShowActivityForm(false);
      setActivityForm({ type: 'note', title: '', body: '', direction: 'outbound' });
      const r = await crmAPI.listActivities({ contact_id: contact.id });
      setActivities(r.data);
    } catch { toast.error('Error al crear'); }
  };

  const handleCreateDeal = async () => {
    if (!dealForm.title.trim()) return toast.error('El título es requerido');
    try {
      await crmAPI.createDeal({ ...dealForm, contact_id: contact.id });
      toast.success('Deal creado ✓');
      setShowDealForm(false);
      const r = await crmAPI.listDeals({ contact_id: contact.id });
      setDeals(r.data);
    } catch { toast.error('Error al crear deal'); }
  };

  const src = sources.find(s => s.id === contact.lead_source_id);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...cardStyle, width: 'min(720px,96vw)', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ width: '50px', height: '50px', borderRadius: '50%', background: '#7c3aed30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '800', color: '#a78bfa' }}>{(contact.first_name?.[0] || '?').toUpperCase()}</div>
            <div>
              <div style={{ fontSize: '18px', fontWeight: '800' }}>{contact.first_name} {contact.last_name}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{contact.position}{contact.position && contact.company ? ' · ' : ''}{contact.company}</div>
              {src && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '5px', background: `${src.color}20`, color: src.color }}>{src.name}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={onEdit} style={{ padding: '6px 12px', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>✏️ Editar</button>
            <button onClick={onClose} style={{ padding: '6px 12px', borderRadius: '7px', border: 'none', background: 'var(--bg-elevated)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>×</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px' }}>
          {contact.email && <div style={{ ...cardStyle, padding: '10px', background: 'var(--bg-elevated)' }}><div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>EMAIL</div><div style={{ fontSize: '12px', marginTop: '2px' }}>{contact.email}</div></div>}
          {contact.phone && <div style={{ ...cardStyle, padding: '10px', background: 'var(--bg-elevated)' }}><div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>TELÉFONO</div><div style={{ fontSize: '12px', marginTop: '2px' }}>{contact.phone}</div></div>}
          {contact.whatsapp && <div style={{ ...cardStyle, padding: '10px', background: 'var(--bg-elevated)' }}><div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>WHATSAPP</div><div style={{ fontSize: '12px', marginTop: '2px' }}>{contact.whatsapp}</div></div>}
        </div>

        <div style={{ ...cardStyle, background: 'linear-gradient(135deg,#1e1040,#2d1b69)', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#a78bfa' }}>🧠 ARIA — Análisis del lead</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleScore} style={{ padding: '5px 12px', borderRadius: '6px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>✨ Score</button>
              <button onClick={handleBriefing} disabled={loadingBriefing} style={{ padding: '5px 12px', borderRadius: '6px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>{loadingBriefing ? '...' : '📋 Briefing'}</button>
            </div>
          </div>
          {score && (
            <div style={{ marginTop: '12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '800', color: SCORE_COLOR(score.score) }}>{score.score}<span style={{ fontSize: '14px' }}>/100</span></div>
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: SCORE_COLOR(score.score) }}>{SCORE_LABEL(score.classification)}</div>
                <div style={{ fontSize: '12px', color: '#e2d9f3', marginTop: '2px' }}>{score.reason}</div>
              </div>
            </div>
          )}
          {briefing && <div style={{ marginTop: '12px', padding: '12px', background: '#ffffff08', borderRadius: '8px', fontSize: '12px', color: '#e2d9f3', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{briefing}</div>}
        </div>

        <div style={{ ...cardStyle, marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '10px' }}>⚡ Registrar actividad con IA</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input style={{ ...inputStyle, flex: 1 }} value={activityText} onChange={e => setActivityText(e.target.value)} placeholder="Ej: Llamé a Juan, está interesado pero pide precio para el viernes..." />
            <button disabled={extracting} onClick={handleExtract} style={{ padding: '9px 14px', borderRadius: '8px', border: 'none', background: extracting ? '#7c3aed90' : '#7c3aed', color: '#fff', fontSize: '12px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>{extracting ? '...' : '🧠 IA'}</button>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700' }}>💼 Deals ({deals.length})</div>
            <button onClick={() => setShowDealForm(true)} style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '11px', cursor: 'pointer' }}>+ Deal</button>
          </div>
          {showDealForm && (
            <div style={{ ...cardStyle, background: 'var(--bg-elevated)', marginBottom: '10px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Título</label><input style={inputStyle} value={dealForm.title} onChange={e => setDealForm(f => ({ ...f, title: e.target.value }))} /></div>
                <div><label style={labelStyle}>Valor</label><input style={inputStyle} type="number" value={dealForm.value} onChange={e => setDealForm(f => ({ ...f, value: parseFloat(e.target.value) }))} /></div>
                <div><label style={labelStyle}>Etapa</label>
                  <select style={inputStyle} value={dealForm.stage_id} onChange={e => setDealForm(f => ({ ...f, stage_id: e.target.value }))}>
                    <option value="">Primera etapa</option>
                    {pipeline.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button onClick={() => setShowDealForm(false)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>Cancelar</button>
                <button onClick={handleCreateDeal} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Crear</button>
              </div>
            </div>
          )}
          {deals.map(d => {
            const stage = pipeline.find(s => s.id === d.stage_id);
            return (
              <div key={d.id} style={{ ...cardStyle, background: 'var(--bg-elevated)', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>{d.title}</div>
                  {stage && <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: `${stage.color}20`, color: stage.color }}>{stage.name}</span>}
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#10b981' }}>{fmtCurrency(d.value, d.currency)}</div>
              </div>
            );
          })}
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700' }}>📋 Historial ({activities.length})</div>
            <button onClick={() => setShowActivityForm(!showActivityForm)} style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '11px', cursor: 'pointer' }}>+ Actividad</button>
          </div>
          {showActivityForm && (
            <div style={{ ...cardStyle, background: 'var(--bg-elevated)', marginBottom: '10px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div><label style={labelStyle}>Tipo</label>
                  <select style={inputStyle} value={activityForm.type} onChange={e => setActivityForm(f => ({ ...f, type: e.target.value }))}>
                    {Object.keys(TYPE_ICON).map(t => <option key={t} value={t}>{TYPE_ICON[t]} {t}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Título</label><input style={inputStyle} value={activityForm.title} onChange={e => setActivityForm(f => ({ ...f, title: e.target.value }))} /></div>
                <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Detalle</label><textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={activityForm.body} onChange={e => setActivityForm(f => ({ ...f, body: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button onClick={() => setShowActivityForm(false)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>Cancelar</button>
                <button onClick={handleCreateActivity} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Guardar</button>
              </div>
            </div>
          )}
          {activities.map(a => (
            <div key={a.id} style={{ ...cardStyle, background: 'var(--bg-elevated)', display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '6px' }}>
              <span style={{ fontSize: '18px' }}>{TYPE_ICON[a.type] || '📝'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>{a.title || a.type}</div>
                {a.body && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{a.body}</div>}
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{new Date(a.created_at + 'Z').toLocaleString('es-CO')}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// CONTACTS TAB
// ════════════════════════════════════════════════════════════
function ContactsTab({ sources, pipeline }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = { first_name: '', last_name: '', email: '', phone: '', whatsapp: '', company: '', position: '', city: '', country: 'CO', notes: '', status: 'lead', lead_source_id: '' };
  const [form, setForm] = useState(emptyForm);
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await crmAPI.listContacts({ search: search || undefined, limit: 100 }); setContacts(r.data); }
    catch { toast.error('Error cargando contactos'); }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.first_name.trim() && !form.email.trim() && !form.phone.trim()) return toast.error('Ingresa al menos nombre, email o teléfono');
    setSaving(true);
    try {
      if (selected) { await crmAPI.updateContact(selected.id, form); toast.success('Contacto actualizado ✓'); }
      else { await crmAPI.createContact(form); toast.success('Contacto creado ✓'); }
      setShowForm(false); setSelected(null); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
    setSaving(false);
  };

  const handleEdit = (c) => { setSelected(c); setForm({ ...emptyForm, ...c }); setShowForm(true); };
  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar contacto?')) return;
    try { await crmAPI.deleteContact(id); toast.success('Eliminado'); load(); }
    catch { toast.error('Error'); }
  };

  const handleExport = async () => {
    try {
      const { default: api } = await import('../api/client');
      const res = await api.get('/api/v1/crm/contacts/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'contactos.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Exportado ✓');
    } catch { toast.error('Error al exportar'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input style={{ ...inputStyle, maxWidth: '280px' }} placeholder="🔍 Buscar..." value={search} onChange={e => setSearch(e.target.value)} />
        <button onClick={() => { setSelected(null); setForm(emptyForm); setShowForm(true); }} style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>+ Nuevo</button>
        <button onClick={handleExport} style={{ padding: '9px 16px', borderRadius: '8px', border: '1px solid #10b98140', background: '#10b98115', color: '#10b981', fontSize: '13px', cursor: 'pointer', fontWeight: '600' }}>⬇️ CSV</button>
      </div>
      {loading ? <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>Cargando...</div>
        : contacts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '48px', marginBottom: '8px' }}>👥</div>
            <div>No hay contactos</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {contacts.map(c => {
              const st = STATUSES.find(s => s.v === c.status) || STATUSES[0];
              return (
                <div key={c.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => setShowDetail(c)}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#7c3aed30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '700', color: '#a78bfa', flexShrink: 0 }}>{(c.first_name?.[0] || '?').toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '700', fontSize: '14px' }}>{c.first_name} {c.last_name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{c.company || c.email || c.phone}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', background: `${st.c}20`, color: st.c, border: `1px solid ${st.c}40` }}>{st.l}</span>
                    <button onClick={e => { e.stopPropagation(); handleEdit(c); }} style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(c.id); }} style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #ef444430', background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: '12px' }}>🗑</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={{ ...cardStyle, width: 'min(600px,96vw)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontSize: '16px', fontWeight: '800', marginBottom: '20px' }}>{selected ? '✏️ Editar contacto' : '+ Nuevo contacto'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div><label style={labelStyle}>Nombre</label><input style={inputStyle} value={form.first_name} onChange={e => set('first_name')(e.target.value)} placeholder="Juan" /></div>
              <div><label style={labelStyle}>Apellido</label><input style={inputStyle} value={form.last_name} onChange={e => set('last_name')(e.target.value)} placeholder="Pérez" /></div>
              <div><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={form.email} onChange={e => set('email')(e.target.value)} /></div>
              <div><label style={labelStyle}>Teléfono</label><input style={inputStyle} value={form.phone} onChange={e => set('phone')(e.target.value)} /></div>
              <div><label style={labelStyle}>WhatsApp</label><input style={inputStyle} value={form.whatsapp} onChange={e => set('whatsapp')(e.target.value)} /></div>
              <div><label style={labelStyle}>Empresa</label><input style={inputStyle} value={form.company} onChange={e => set('company')(e.target.value)} /></div>
              <div><label style={labelStyle}>Cargo</label><input style={inputStyle} value={form.position} onChange={e => set('position')(e.target.value)} /></div>
              <div><label style={labelStyle}>Ciudad</label><input style={inputStyle} value={form.city} onChange={e => set('city')(e.target.value)} /></div>
              <div><label style={labelStyle}>Estado</label>
                <select style={inputStyle} value={form.status} onChange={e => set('status')(e.target.value)}>
                  {STATUSES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Fuente</label>
                <select style={inputStyle} value={form.lead_source_id} onChange={e => set('lead_source_id')(e.target.value)}>
                  <option value="">Sin fuente</option>
                  {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '12px' }}><label style={labelStyle}>Notas</label><textarea style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} value={form.notes} onChange={e => set('notes')(e.target.value)} /></div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
              <button disabled={saving} onClick={handleSave} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: saving ? '#7c3aed90' : '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>{saving ? 'Guardando...' : '💾 Guardar'}</button>
            </div>
          </div>
        </div>
      )}
      {showDetail && <ContactDetail contact={showDetail} sources={sources} pipeline={pipeline} onClose={() => setShowDetail(null)} onEdit={() => { handleEdit(showDetail); setShowDetail(null); }} onRefresh={load} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PIPELINE / KANBAN TAB
// ════════════════════════════════════════════════════════════
function PipelineTab({ pipeline }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [editDeal, setEditDeal] = useState(null);
  const [dealForm, setDealForm] = useState({ value: 0, probability: 0, stage_id: '', title: '', notes: '', contact_id: '' });
  const [savingDeal, setSavingDeal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showQuickContact, setShowQuickContact] = useState(false);
  const [quickContactName, setQuickContactName] = useState('');
  const [creatingContact, setCreatingContact] = useState(false);

  useEffect(() => {
    crmAPI.listDeals().then(r => setDeals(r.data)).catch(() => {}).finally(() => setLoading(false));
    crmAPI.listContacts({ limit: 200 }).then(r => setContacts(r.data)).catch(() => {});
  }, []);

  const openNewDeal = () => {
    setEditDeal({ _new: true });
    setDealForm({ value: 0, probability: 0, stage_id: pipeline[0]?.id || '', title: '', notes: '', contact_id: '' });
  };

  const createDeal = async () => {
    if (!dealForm.contact_id) return toast.error('Selecciona o crea un contacto para el negocio');
    setSavingDeal(true);
    try {
      const r = await crmAPI.createDeal(dealForm);
      setDeals(prev => [...prev, r.data]);
      toast.success('Deal creado ✓');
      setEditDeal(null);
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Error creando deal'); }
    setSavingDeal(false);
  };

  const createQuickContact = async () => {
    if (!quickContactName.trim()) return toast.error('Escribe un nombre para el contacto');
    setCreatingContact(true);
    try {
      const r = await crmAPI.createContact({ first_name: quickContactName.trim() });
      setContacts(prev => [...prev, r.data]);
      setDealForm(f => ({ ...f, contact_id: r.data.id }));
      setQuickContactName('');
      setShowQuickContact(false);
      toast.success('Contacto creado ✓');
    } catch { toast.error('Error creando contacto'); }
    setCreatingContact(false);
  };

  const handleDrop = async (stageId) => {
    if (!dragging || dragging.stage_id === stageId) return;
    try {
      await crmAPI.updateDeal(dragging.id, { stage_id: stageId });
      setDeals(prev => prev.map(d => d.id === dragging.id ? { ...d, stage_id: stageId } : d));
      toast.success('Deal movido ✓');
    } catch { toast.error('Error'); }
    setDragging(null);
  };

  const openDealEdit = (deal) => {
    setEditDeal(deal);
    setDealForm({ value: deal.value || 0, probability: deal.probability || 0, stage_id: deal.stage_id || '', title: deal.title || '', notes: deal.notes || '' });
  };

  const saveDeal = async (extraStatus) => {
    setSavingDeal(true);
    try {
      const payload = { ...dealForm };
      if (extraStatus) payload.status = extraStatus;
      await crmAPI.updateDeal(editDeal.id, payload);
      if (extraStatus === 'won' || extraStatus === 'lost') {
        toast.success(extraStatus === 'won' ? 'Deal ganado 🎉' : 'Deal marcado como perdido');
        // Recargar deals desde DB para reflejar el nuevo stage_id (incluyendo ganados/perdidos)
        crmAPI.listDeals().then(r => setDeals(r.data)).catch(() => {});
      } else {
        setDeals(prev => prev.map(d => d.id === editDeal.id ? { ...d, ...payload } : d));
        toast.success('Deal actualizado ✓');
      }
      setEditDeal(null);
    } catch { toast.error('Error guardando'); }
    setSavingDeal(false);
  };

  const containerRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    const el = containerRef.current;
    if (!el) return;
    const { left, right } = el.getBoundingClientRect();
    const x = e.clientX;
    const zone = 80;
    if (x < left + zone) el.scrollLeft -= 12;
    else if (x > right - zone) el.scrollLeft += 12;
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>Cargando...</div>;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <button onClick={openNewDeal} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>+ Nuevo Deal</button>
      </div>
      <div ref={containerRef} onDragOver={handleDragOver} style={{ overflowX: 'auto', paddingBottom: '8px' }}>
      <div style={{ display: 'flex', gap: '16px', minWidth: 'max-content', paddingBottom: '16px', alignItems: 'flex-start' }}>
        {pipeline.map(stage => {
          const stageDeals = deals.filter(d => d.stage_id === stage.id);
          const total = stageDeals.reduce((a, d) => a + (d.value || 0), 0);
          return (
            <div key={stage.id} style={{ width: '280px', minWidth: '280px', background: 'var(--bg-elevated)', borderRadius: '12px', padding: '14px', borderTop: `3px solid ${stage.color}` }}
              onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(stage.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700' }}>{stage.name}</div>
                <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', background: `${stage.color}20`, color: stage.color }}>{stageDeals.length}</span>
              </div>
              {total > 0 && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>{fmtCurrency(total)}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {stageDeals.map(deal => (
                  <div key={deal.id} draggable onDragStart={() => setDragging(deal)} onClick={() => openDealEdit(deal)}
                    style={{ background: 'var(--bg-card)', borderRadius: '8px', padding: '10px', cursor: 'pointer', border: '1px solid var(--border)', position: 'relative' }}>
                    <button onClick={e => { e.stopPropagation(); if(window.confirm('¿Eliminar este deal?')) crmAPI.updateDeal(deal.id, { status: 'lost' }).then(() => { setDeals(prev => prev.filter(d => d.id !== deal.id)); toast.success('Deal eliminado'); }).catch(() => toast.error('Error')); }} style={{ position: 'absolute', top: '6px', right: '6px', background: '#ef444420', border: 'none', borderRadius: '4px', color: '#ef4444', cursor: 'pointer', fontSize: '11px', padding: '2px 5px', lineHeight: 1 }}>🗑</button>
                    <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', paddingRight: '20px' }}>{deal.title}</div>
                    {deal.notes && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: '1.4' }}>{deal.notes.slice(0, 60)}{deal.notes.length > 60 ? '...' : ''}</div>}
                    <div style={{ fontSize: '12px', color: '#10b981', fontWeight: '700' }}>{fmtCurrency(deal.value, deal.currency)}</div>
                  </div>
                ))}
                {stageDeals.length === 0 && <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>Sin deals</div>}
              </div>
            </div>
          );
        })}
      </div>

      </div>
      {editDeal && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px' }} onClick={() => setEditDeal(null)}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '14px', width: 'min(480px, 96vw)', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #7c3aed40' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg,#1e1040,#2d1b69)' }}>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#fff' }}>{editDeal?._new ? '💼 Nuevo Deal' : '💼 Gestionar oportunidad'}</div>
              <button onClick={() => setEditDeal(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '20px 22px' }}>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Título *</label>
                <input style={inputStyle} value={dealForm.title} onChange={e => setDealForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: Propuesta comercial empresa X" />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Contacto *</label>
                {!showQuickContact ? (
                  <>
                    <select style={inputStyle} value={dealForm.contact_id} onChange={e => setDealForm(f => ({ ...f, contact_id: e.target.value }))}>
                      <option value="" disabled>Selecciona un contacto...</option>
                      {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company ? `· ${c.company}` : ''}</option>)}
                    </select>
                    <button type="button" onClick={() => setShowQuickContact(true)} style={{ marginTop: '6px', background: 'none', border: 'none', color: '#a78bfa', fontSize: '12px', cursor: 'pointer', padding: 0 }}>+ Crear contacto rápido</button>
                  </>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      autoFocus
                      style={{ ...inputStyle, flex: 1 }}
                      value={quickContactName}
                      onChange={e => setQuickContactName(e.target.value)}
                      placeholder="Nombre del contacto"
                      onKeyDown={e => e.key === 'Enter' && createQuickContact()}
                    />
                    <button type="button" disabled={creatingContact} onClick={createQuickContact} style={{ padding: '0 14px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>{creatingContact ? '...' : '💾'}</button>
                    <button type="button" onClick={() => { setShowQuickContact(false); setQuickContactName(''); }} style={{ padding: '0 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' }}>✕</button>
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={labelStyle}>Valor (COP)</label>
                  <input type="number" style={inputStyle} value={dealForm.value} onChange={e => setDealForm(f => ({ ...f, value: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div>
                  <label style={labelStyle}>Probabilidad (%)</label>
                  <input type="number" min="0" max="100" style={inputStyle} value={dealForm.probability} onChange={e => setDealForm(f => ({ ...f, probability: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Etapa</label>
                <select style={inputStyle} value={dealForm.stage_id} onChange={e => setDealForm(f => ({ ...f, stage_id: e.target.value }))}>
                  {pipeline.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <label style={{ ...labelStyle, color: '#a78bfa' }}>📋 Nota original del agente (referencia)</label>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5', marginTop: '4px' }}>{editDeal.notes || 'Sin nota'}</div>
              </div>
              <div style={{ marginBottom: '18px' }}>
                <label style={labelStyle}>Notas / observaciones</label>
                <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={dealForm.notes} onChange={e => setDealForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <button onClick={() => saveDeal('won')} disabled={savingDeal} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>✅ Ganado</button>
                <button onClick={() => saveDeal('lost')} disabled={savingDeal} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>❌ Perdido</button>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setEditDeal(null)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
                {editDeal?._new
                  ? <button onClick={createDeal} disabled={savingDeal} style={{ flex: 2, padding: '10px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>{savingDeal ? 'Creando...' : '✅ Crear Deal'}</button>
                  : <button onClick={() => saveDeal(null)} disabled={savingDeal} style={{ flex: 2, padding: '10px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>{savingDeal ? 'Guardando...' : 'Guardar cambios'}</button>
                }
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ACTIVITIES TAB
// ════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════
// CALENDAR TAB — Citas
// ════════════════════════════════════════════════════════════
function CalendarTab({ contacts }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', appointment_date: '', appointment_time: '09:00', duration_minutes: 60, type: 'meeting', status: 'scheduled', contact_id: '', location: '', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await crmAPI.listAppointments(); setAppointments(r.data); }
    catch { toast.error('Error cargando citas'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const dayNames = ['D','L','M','X','J','V','S'];

  const apptsByDay = {};
  appointments.forEach(a => {
    const d = new Date(a.appointment_date.replace(' ','T')+'Z');
    const dCO = new Date(d.getTime() - 5*60*60*1000);
    if (dCO.getUTCFullYear() === year && dCO.getUTCMonth() === month) {
      const day = dCO.getUTCDate();
      if (!apptsByDay[day]) apptsByDay[day] = [];
      apptsByDay[day].push(a);
    }
  });

  const TYPE_COLOR = { meeting: '#7c3aed', call: '#3b82f6', whatsapp: '#10b981', email: '#f59e0b', other: '#6b7280' };
  const STATUS_COLOR = { scheduled: '#3b82f6', completed: '#10b981', cancelled: '#ef4444' };

  const pad = n => String(n).padStart(2, '0');

  const openNew = (day) => {
    const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
    setEditing(null);
    setForm({ title: '', appointment_date: dateStr, appointment_time: '09:00', duration_minutes: 60, type: 'meeting', status: 'scheduled', contact_id: '', location: '', notes: '' });
    setShowModal(true);
  };

  const openEdit = (a) => {
    const d = new Date(a.appointment_date);
    setEditing(a);
    setForm({
      title: a.title || '',
      appointment_date: (() => { const dz = new Date(a.appointment_date.replace(' ','T')+'Z'); const co = new Date(dz.getTime() - 5*60*60*1000); return `${co.getUTCFullYear()}-${pad(co.getUTCMonth()+1)}-${pad(co.getUTCDate())}`; })(),
      appointment_time: `${pad((new Date(a.appointment_date.replace(' ','T')+'Z').getUTCHours()-5+24)%24)}:${pad(new Date(a.appointment_date.replace(' ','T')+'Z').getUTCMinutes())}`,
      duration_minutes: a.duration_minutes || 60,
      type: a.type || 'meeting',
      status: a.status || 'scheduled',
      contact_id: a.contact_id || '',
      location: a.location || '',
      notes: a.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('El título es requerido');
    if (!form.appointment_date) return toast.error('La fecha es requerida');
    const payload = {
      title: form.title,
      appointment_date: (() => { const [h,m] = form.appointment_time.split(':'); const d = new Date(`${form.appointment_date}T${String(Number(h)+5).padStart(2,'0')}:${m}:00Z`); return d.toISOString().slice(0,19); })(),
      duration_minutes: Number(form.duration_minutes),
      type: form.type,
      status: form.status,
      contact_id: form.contact_id || null,
      location: form.location || null,
      notes: form.notes || null,
    };
    try {
      if (editing) { await crmAPI.updateAppointment(editing.id, payload); toast.success('Cita actualizada'); }
      else { await crmAPI.createAppointment(payload); toast.success('Cita creada'); }
      setShowModal(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
  };

  const [deletedAppts, setDeletedAppts] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const loadDeleted = useCallback(async () => {
    try { const r = await crmAPI.listDeletedAppointments(); setDeletedAppts(r.data); }
    catch {}
  }, []);

  useEffect(() => { if (showDeleted) loadDeleted(); }, [showDeleted, loadDeleted]);

  const handleDelete = async (appt) => {
    const msg = appt.calendar_event_id
      ? '¿Eliminar esta cita? También se eliminará de Google Calendar.'
      : '¿Eliminar esta cita?';
    if (!window.confirm(msg)) return;
    try { await crmAPI.deleteAppointment(appt.id); toast.success('Cita eliminada'); load(); if (showDeleted) loadDeleted(); }
    catch { toast.error('Error al eliminar'); }
  };

  const today = new Date();
  const isToday = (day) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  const monthAppts = appointments
    .filter(a => { const d = new Date(a.appointment_date.replace(' ','T')+'Z'); const dCO = new Date(d.getTime()-5*60*60*1000); return dCO.getUTCFullYear() === year && dCO.getUTCMonth() === month; })
    .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));

  const sortedDeletedAppts = [...deletedAppts].sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));

  return (
    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>

      {/* Header navegación */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '6px', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#fff', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}>‹</button>
          <div style={{ fontSize: '15px', fontWeight: '700', minWidth: '130px', textAlign: 'center' }}>{monthNames[month]} {year}</div>
          <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#fff', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}>›</button>
          <button onClick={() => setCurrentDate(new Date())} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: '8px', padding: '5px 8px', cursor: 'pointer', fontSize: '11px' }}>Hoy</button>
        </div>
        <div style={{ display:'flex', gap:'6px' }}>
          <button onClick={() => setShowDeleted(v => !v)} style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid #ef444440', background: showDeleted ? '#ef444422' : 'var(--bg-elevated)', color: showDeleted ? '#ef4444' : 'var(--text-muted)', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>🗑 Eliminadas</button>
          <button onClick={() => openNew(today.getDate())} style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Nueva cita</button>
        </div>
      </div>

      {/* Días de la semana */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '2px' }}>
        {dayNames.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', padding: '4px 0' }}>{d}</div>
        ))}
      </div>

      {/* Grid calendario */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Cargando...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`e${i}`} style={{ minHeight: '60px', background: 'var(--bg-elevated)', borderRadius: '6px', opacity: 0.2 }} />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
            <div
              key={day}
              onClick={() => openNew(day)}
              style={{
                minHeight: '60px',
                background: isToday(day) ? '#7c3aed22' : 'var(--bg-elevated)',
                border: isToday(day) ? '1px solid #7c3aed' : '1px solid var(--border)',
                borderRadius: '6px',
                padding: '4px',
                cursor: 'pointer',
                overflow: 'hidden',
              }}
            >
              <div style={{ fontSize: '11px', fontWeight: isToday(day) ? '800' : '600', color: isToday(day) ? '#a78bfa' : '#fff', marginBottom: '2px' }}>{day}</div>
              {(apptsByDay[day] || []).slice(0, 2).map(a => (
                <div
                  key={a.id}
                  onClick={e => { e.stopPropagation(); openEdit(a); }}
                  style={{ background: TYPE_COLOR[a.type] || '#6b7280', borderRadius: '3px', padding: '1px 3px', fontSize: '9px', color: '#fff', marginBottom: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                >
                  {new Date(a.appointment_date.replace(' ','T')+'Z').toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Bogota' })} {a.title}
                </div>
              ))}
              {(apptsByDay[day] || []).length > 2 && (
                <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>+{(apptsByDay[day].length - 2)} más</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lista citas del mes */}
      <div style={{ marginTop: '20px' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '10px', color: 'var(--text-secondary)' }}>📋 Citas de {monthNames[month]} ({monthAppts.length})</div>
        {monthAppts.length === 0
          ? <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No hay citas este mes.</div>
          : monthAppts.map(a => {
              const d = new Date(a.appointment_date);
              const contact = contacts?.find(c => c.id === a.contact_id);
              return (
                <div key={a.id} style={{ ...cardStyle, marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: STATUS_COLOR[a.status] || '#6b7280', flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: '700', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        📅 {new Date(a.appointment_date.replace(' ','T')+'Z').toLocaleDateString('es-CO', { timeZone: 'America/Bogota' })} {new Date(a.appointment_date.replace(' ','T')+'Z').toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Bogota' })}
                        {a.duration_minutes ? ` · ${a.duration_minutes}min` : ''}
                        {contact ? ` · 👤 ${contact.first_name} ${contact.last_name}` : ''}
                        {a.location ? ` · 📍 ${a.location}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button onClick={() => openEdit(a)} style={{ padding: '4px 8px', borderRadius: '6px', border: 'none', background: 'var(--bg-elevated)', color: '#fff', fontSize: '11px', cursor: 'pointer' }}>✏️</button>
                    <button onClick={() => handleDelete(a)} style={{ padding: '4px 8px', borderRadius: '6px', border: 'none', background: '#ef444422', color: '#ef4444', fontSize: '11px', cursor: 'pointer' }}>🗑</button>
                  </div>
                </div>
              );
            })
        }
      </div>

      {/* Eliminadas */}
      {showDeleted && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '10px', color: '#ef4444' }}>🗑 Citas Eliminadas ({deletedAppts.length})</div>
          {deletedAppts.length === 0
            ? <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No hay citas eliminadas.</div>
            : sortedDeletedAppts.map(a => {
                const contact = contacts?.find(c => c.id === a.contact_id);
                return (
                  <div key={a.id} style={{ ...cardStyle, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px', opacity: 0.7, border: '1px solid #ef444430' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '700', fontSize: '13px', textDecoration: 'line-through', color: 'var(--text-primary)' }}>{a.title}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        📅 {new Date(a.appointment_date.replace(' ','T')+'Z').toLocaleDateString('es-CO', { timeZone: 'America/Bogota' })} {new Date(a.appointment_date.replace(' ','T')+'Z').toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Bogota' })}
                        {contact ? ` · 👤 ${contact.first_name} ${contact.last_name}` : ''}
                        {' · '}🗑 {new Date(a.deleted_at).toLocaleDateString('es-CO', { timeZone: 'America/Bogota', day: 'numeric', month: 'short', year: 'numeric' })}
                        {a.calendar_event_id ? ' · 📆 Google Calendar' : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', background: '#ef444420', color: '#ef4444', flexShrink: 0 }}>Eliminada</span>
                  </div>
                );
              })
          }
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000aa', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0' }} onClick={() => setShowModal(false)}>
          <div style={{ ...cardStyle, width: '100%', maxWidth: '520px', maxHeight: '92vh', overflowY: 'auto', borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: '36px', height: '4px', background: 'var(--border)', borderRadius: '2px', margin: '0 auto 16px' }} />
            <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '16px' }}>{editing ? '✏️ Editar cita' : '📅 Nueva cita'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div><label style={labelStyle}>Título *</label><input style={inputStyle} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: Reunión con cliente" /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div><label style={labelStyle}>Fecha *</label><input type="date" style={inputStyle} value={form.appointment_date} min={new Date().toISOString().split("T")[0]} onChange={e => setForm(f => ({ ...f, appointment_date: e.target.value }))} /></div>
                <div><label style={labelStyle}>Hora Colombia (HH:MM)</label><input type="text" placeholder="14:00" pattern="[0-9]{2}:[0-9]{2}" style={inputStyle} value={form.appointment_time} onChange={e => setForm(f => ({ ...f, appointment_time: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div><label style={labelStyle}>Tipo</label>
                  <select style={inputStyle} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="meeting">🤝 Reunión</option>
                    <option value="call">📞 Llamada</option>
                    <option value="whatsapp">💬 WhatsApp</option>
                    <option value="email">📧 Email</option>
                    <option value="other">📌 Otro</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Duración (min)</label><input type="number" style={inputStyle} value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} /></div>
              </div>
              <div><label style={labelStyle}>Estado</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="scheduled">Programada</option>
                  <option value="completed">Completada</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </div>
              <div><label style={labelStyle}>Contacto</label>
                <select style={inputStyle} value={form.contact_id} onChange={e => setForm(f => ({ ...f, contact_id: e.target.value }))}>
                  <option value="">Sin contacto</option>
                  {(contacts || []).map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.company ? ` · ${c.company}` : ''}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Ubicación</label><input style={inputStyle} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Oficina, Meet, Zoom..." /></div>
              <div><label style={labelStyle}>Notas</label><textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
              <div style={{ display: 'flex', gap: '8px', paddingBottom: '8px' }}>
                <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '11px', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
                <button onClick={handleSave} style={{ flex: 2, padding: '11px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>💾 Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// BUSINESS HOURS TAB — Horarios de atención
// ════════════════════════════════════════════════════════════
function BusinessHoursTab() {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await crmAPI.getBusinessHours(); setHours(r.data); }
    catch { toast.error('Error cargando horarios'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (day, field, value) => {
    setHours(prev => prev.map(h => h.day_of_week === day ? { ...h, [field]: value } : h));
  };

  const handleSave = async (h) => {
    setSaving(h.day_of_week);
    try {
      await crmAPI.updateBusinessHours(h.day_of_week, {
        is_open: h.is_open,
        open_time: h.open_time,
        close_time: h.close_time,
      });
      toast.success(`${DAY_NAMES[h.day_of_week]} actualizado`);
    } catch { toast.error('Error guardando'); }
    setSaving(null);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Cargando horarios...</div>;

  return (
    <div style={{ maxWidth: '600px' }}>
      <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '6px' }}>🕐 Horarios de atención</div>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px' }}>El agente usará estos horarios para validar citas y recordatorios.</div>

      {hours.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No hay horarios configurados. Verifica que la tabla esté creada en BD.</div>
      )}

      {hours.map(h => (
        <div key={h.day_of_week} style={{ ...cardStyle, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ width: '90px', fontWeight: '700', fontSize: '13px' }}>{DAY_NAMES[h.day_of_week]}</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
            <input type="checkbox" checked={h.is_open} onChange={e => handleChange(h.day_of_week, 'is_open', e.target.checked)} />
            {h.is_open ? '✅ Abierto' : '❌ Cerrado'}
          </label>
          {h.is_open && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Desde</span>
                <input type="time" style={{ ...inputStyle, width: '110px' }} value={h.open_time} onChange={e => handleChange(h.day_of_week, 'open_time', e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Hasta</span>
                <input type="time" style={{ ...inputStyle, width: '110px' }} value={h.close_time} onChange={e => handleChange(h.day_of_week, 'close_time', e.target.value)} />
              </div>
            </>
          )}
          <button
            onClick={() => handleSave(h)}
            disabled={saving === h.day_of_week}
            style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: '7px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer', opacity: saving === h.day_of_week ? 0.6 : 1 }}
          >
            {saving === h.day_of_week ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      ))}
    </div>
  );
}
function ActivitiesTab() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewActivity, setViewActivity] = useState(null);
  const emptyForm = { type: 'note', title: '', body: '', direction: 'outbound', status: 'completed', scheduled_at: '' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await crmAPI.listActivities({ type: typeFilter || undefined, limit: 100 });
      setActivities(r.data);
    } catch { toast.error('Error cargando actividades'); }
    setLoading(false);
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setShowForm(true); setShowModal(false); };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({ type: a.type || 'note', title: a.title || '', body: a.body || '', direction: a.direction || 'outbound', status: a.status || 'completed', scheduled_at: a.scheduled_at ? a.scheduled_at.slice(0, 16) : '' });
    setShowModal(true); setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('El título es requerido');
    setSaving(true);
    try {
      const payload = { ...form, scheduled_at: form.scheduled_at || null };
      if (editingId) { await crmAPI.updateActivity(editingId, payload); toast.success('Actividad actualizada'); }
      else { await crmAPI.createActivity(payload); toast.success('Actividad registrada'); }
      setShowForm(false); setShowModal(false); setEditingId(null); setForm(emptyForm); load();
    } catch { toast.error('Error guardando actividad'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try { await crmAPI.deleteActivity(id); toast.success('Actividad eliminada'); setConfirmDelete(null); load(); }
    catch { toast.error('Error eliminando'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', fontWeight: '700' }}>📋 Actividades ({activities.length})</div>
        <button onClick={openCreate} style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>+ Nueva actividad</button>
      </div>
      {showForm && (
        <div style={{ ...cardStyle, marginBottom: '16px', border: '1px solid #7c3aed60' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '12px', color: '#a78bfa' }}>➕ Nueva actividad</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label style={labelStyle}>Tipo</label>
              <select style={inputStyle} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                <option value="note">📝 Nota</option>
                <option value="call">📞 Llamada</option>
                <option value="whatsapp">💬 WhatsApp</option>
                <option value="meeting">🤝 Reunión</option>
                <option value="email">📧 Email</option>
              </select>
            </div>
            <div><label style={labelStyle}>Dirección</label>
              <select style={inputStyle} value={form.direction} onChange={e => setForm(f => ({ ...f, direction: e.target.value }))}>
                <option value="outbound">↑ Saliente</option>
                <option value="inbound">↓ Entrante</option>
              </select>
            </div>
            <div><label style={labelStyle}>Estado</label>
              <select style={inputStyle} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="completed">✅ Completada</option>
                <option value="pending">⏳ Pendiente</option>
                <option value="scheduled">📅 Programada</option>
              </select>
            </div>
            <div><label style={labelStyle}>Fecha programada (opcional)</label>
              <input type="datetime-local" style={inputStyle} value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} />
            </div>
            <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Título *</label>
              <input style={inputStyle} value={form.title} placeholder="Ej: Llamada de seguimiento con cliente" onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Detalle / Notas</label>
              <textarea style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} value={form.body} placeholder="Descripción de la actividad..." onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} style={{ padding: '7px 14px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '7px 16px', borderRadius: '6px', border: 'none', background: saving ? '#7c3aed90' : '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </div>
      )}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000bb', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '14px', padding: '28px', width: 'min(480px, 95vw)', border: '1px solid #7c3aed60', boxShadow: '0 20px 60px #00000060' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#a78bfa' }}>✏️ Editar actividad</div>
              <button onClick={() => { setShowModal(false); setEditingId(null); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div><label style={labelStyle}>Tipo</label>
                <select style={inputStyle} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="note">📝 Nota</option>
                  <option value="call">📞 Llamada</option>
                  <option value="whatsapp">💬 WhatsApp</option>
                  <option value="meeting">🤝 Reunión</option>
                  <option value="email">📧 Email</option>
                </select>
              </div>
              <div><label style={labelStyle}>Dirección</label>
                <select style={inputStyle} value={form.direction} onChange={e => setForm(f => ({ ...f, direction: e.target.value }))}>
                  <option value="outbound">↑ Saliente</option>
                  <option value="inbound">↓ Entrante</option>
                </select>
              </div>
              <div><label style={labelStyle}>Estado</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="completed">✅ Completada</option>
                  <option value="pending">⏳ Pendiente</option>
                  <option value="scheduled">📅 Programada</option>
                </select>
              </div>
              <div><label style={labelStyle}>Fecha programada (opcional)</label>
                <input type="datetime-local" style={inputStyle} value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Título *</label>
                <input style={inputStyle} value={form.title} placeholder="Ej: Llamada de seguimiento con cliente" onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Detalle / Notas</label>
                <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={form.body} placeholder="Descripción de la actividad..." onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => { setShowModal(false); setEditingId(null); }} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '8px 18px', borderRadius: '6px', border: 'none', background: saving ? '#7c3aed90' : '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>{saving ? 'Guardando...' : 'Actualizar'}</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {['', 'call', 'whatsapp', 'meeting', 'email', 'note'].map(t => (
          <button key={t} onClick={() => setTypeFilter(t)} style={{ padding: '6px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600', background: typeFilter === t ? '#7c3aed' : 'var(--bg-elevated)', color: typeFilter === t ? '#fff' : 'var(--text-muted)' }}>
            {t ? `${TYPE_ICON[t]} ${t}` : '📋 Todas'}
          </button>
        ))}
      </div>
      {loading ? <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>Cargando...</div>
        : activities.length === 0 ? <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}><div style={{ fontSize: '32px' }}>📋</div><div>Sin actividades</div></div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {activities.map(a => {
              const sched = a.scheduled_at ? new Date(a.scheduled_at + 'Z') : null;
              const isPend = a.status !== 'completed' && a.status !== 'cancelled';
              const vencida = sched && isPend && sched < new Date();
              const vigente = sched && isPend && sched >= new Date();
              const accent = vencida ? '#ef4444' : vigente ? '#10b981' : 'transparent';
              return (
              <div key={a.id} style={{ ...cardStyle, display: 'flex', gap: '12px', alignItems: 'flex-start', borderLeft: `3px solid ${accent}`, background: vencida ? '#ef444408' : vigente ? '#10b98108' : cardStyle.background }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#7c3aed20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>{TYPE_ICON[a.type] || '📝'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{a.title || a.type}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(a.created_at + 'Z').toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                      <button onClick={() => setViewActivity(a)} title="Ver" style={{ padding: '3px 8px', borderRadius: '5px', border: 'none', background: '#7c3aed20', color: '#a78bfa', cursor: 'pointer', fontSize: '11px' }}>👁️</button>
                      <button onClick={() => openEdit(a)} title="Editar" style={{ padding: '3px 8px', borderRadius: '5px', border: 'none', background: '#3b82f620', color: '#3b82f6', cursor: 'pointer', fontSize: '11px' }}>✏️</button>
                      <button onClick={() => setConfirmDelete(a.id)} title="Eliminar" style={{ padding: '3px 8px', borderRadius: '5px', border: 'none', background: '#ef444420', color: '#ef4444', cursor: 'pointer', fontSize: '11px' }}>🗑️</button>
                    </div>
                  </div>
                  {a.body && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', lineHeight: '1.5' }}>{a.body.slice(0, 200)}</div>}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '5px', fontSize: '11px', background: '#7c3aed20', color: '#a78bfa' }}>{a.type}</span>
                    <span style={{ padding: '2px 8px', borderRadius: '5px', fontSize: '11px', background: a.direction === 'inbound' ? '#3b82f620' : '#10b98120', color: a.direction === 'inbound' ? '#3b82f6' : '#10b981' }}>{a.direction === 'inbound' ? '↓ Entrante' : '↑ Saliente'}</span>
                    {a.status && a.status !== 'completed' && <span style={{ padding: '2px 8px', borderRadius: '5px', fontSize: '11px', background: '#f59e0b20', color: '#f59e0b' }}>{a.status}</span>}
                    {vencida && <span style={{ padding: '2px 8px', borderRadius: '5px', fontSize: '11px', background: '#ef444420', color: '#ef4444', fontWeight: '600' }}>⚠ Vencida</span>}
                    {vigente && <span style={{ padding: '2px 8px', borderRadius: '5px', fontSize: '11px', background: '#10b98120', color: '#10b981', fontWeight: '600' }}>🕐 {sched.toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      {viewActivity && (() => {
        const a = viewActivity;
        const need = (a.body || '').includes('📌 Necesidad:') ? a.body.split('📌 Necesidad:')[1].trim() : null;
        const convText = need ? a.body.split('📌 Necesidad:')[0].trim() : (a.body || '');
        const lines = convText.split('\n').filter(l => l.trim());
        const isChat = lines.some(l => l.startsWith('Cliente:') || l.startsWith('Agente:'));
        return (
          <div style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px' }} onClick={() => setViewActivity(null)}>
            <div style={{ background: 'var(--bg-elevated)', borderRadius: '14px', width: 'min(560px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', border: '1px solid #7c3aed40', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg,#1e1040,#2d1b69)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#7c3aed30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>{TYPE_ICON[a.type] || '📝'}</div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#fff' }}>{a.title || a.type}</div>
                    <div style={{ fontSize: '11px', color: '#a78bfa' }}>{new Date(a.created_at + 'Z').toLocaleString('es-CO', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                </div>
                <button onClick={() => setViewActivity(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '11px', background: '#7c3aed20', color: '#a78bfa', fontWeight: '600' }}>{a.type}</span>
                  <span style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '11px', background: a.direction === 'inbound' ? '#3b82f620' : '#10b98120', color: a.direction === 'inbound' ? '#3b82f6' : '#10b981', fontWeight: '600' }}>{a.direction === 'inbound' ? '↓ Entrante' : '↑ Saliente'}</span>
                  <span style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '11px', background: '#10b98120', color: '#10b981', fontWeight: '600' }}>{a.status || 'completed'}</span>
                </div>
                {isChat ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {lines.map((line, i) => {
                      const isClient = line.startsWith('Cliente:');
                      const isAgent = line.startsWith('Agente:');
                      const txt = line.replace(/^(Cliente:|Agente:)\s*/, '');
                      if (!isClient && !isAgent) return <div key={i} style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', padding: '4px' }}>{line}</div>;
                      return (
                        <div key={i} style={{ display: 'flex', justifyContent: isClient ? 'flex-start' : 'flex-end' }}>
                          <div style={{ maxWidth: '78%', padding: '8px 12px', borderRadius: isClient ? '12px 12px 12px 3px' : '12px 12px 3px 12px', background: isClient ? 'var(--bg-card)' : '#7c3aed', color: isClient ? 'var(--text-primary)' : '#fff', fontSize: '13px', lineHeight: '1.45' }}>
                            <div style={{ fontSize: '10px', opacity: 0.7, marginBottom: '2px', fontWeight: '600' }}>{isClient ? '👤 Cliente' : '🤖 Agente'}</div>
                            {txt}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{convText || 'Sin contenido'}</div>
                )}
                {need && (
                  <div style={{ marginTop: '18px', padding: '14px', borderRadius: '10px', background: '#f59e0b15', border: '1px solid #f59e0b40' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#f59e0b', marginBottom: '4px' }}>📌 Conclusión / Necesidad</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.5' }}>{need}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000aa', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '12px', padding: '24px', maxWidth: '320px', width: '90%', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '8px' }}>¿Eliminar actividad?</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>Esta acción no se puede deshacer.</div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '7px 14px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>Cancelar</button>
              <button onClick={() => handleDelete(confirmDelete)} style={{ padding: '7px 14px', borderRadius: '6px', border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// EQUIPO TAB
// ════════════════════════════════════════════════════════════
function EquipoTab({ tenant }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await crmAPI.getTenantUsers(); setMembers(r.data); }
    catch { }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSearch = async () => {
    if (searchEmail.length < 3) return;
    setSearching(true);
    try {
      const { default: api } = await import('../api/client');
      const r = await api.get(`/crm/tenants/mine/search-users?email=${encodeURIComponent(searchEmail)}`);
      setSearchResults(r.data);
      setSearched(true);
    } catch { toast.error('Error buscando usuarios'); }
    setSearching(false);
  };

  const handleAdd = async (userId, role = 'vendedor') => {
    try {
      await crmAPI.addTenantUser({ user_id: userId, role });
      toast.success('Usuario agregado ✓');
      setSearchResults([]);
      setSearchEmail('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const handleChangeRole = async (userId, role) => {
    try {
      const { default: api } = await import('../api/client');
      await api.patch(`/crm/tenants/mine/users/${userId}`, { role });
      toast.success('Rol actualizado ✓');
      load();
    } catch { toast.error('Error'); }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('¿Remover este usuario del equipo?')) return;
    try {
      const { default: api } = await import('../api/client');
      await api.delete(`/crm/tenants/mine/users/${userId}`);
      toast.success('Usuario removido');
      load();
    } catch { toast.error('Error'); }
  };

  const ROLES = ['admin', 'gerente', 'vendedor', 'agente'];
  const ROLE_COLOR = { admin: '#ef4444', gerente: '#f59e0b', vendedor: '#3b82f6', agente: '#10b981' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={cardStyle}>
        <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px' }}>➕ Agregar miembro por email</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input style={{ ...inputStyle, flex: 1 }} value={searchEmail} onChange={e => { setSearchEmail(e.target.value); setSearched(false); }}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Escribe el email EXACTO del usuario registrado en TXMark..." />
          <button onClick={handleSearch} disabled={searching} style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '13px', cursor: 'pointer', fontWeight: '700', whiteSpace: 'nowrap' }}>
            {searching ? '...' : '🔍 Buscar'}
          </button>
        </div>
        {searchResults.length > 0 && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {searchResults.map(u => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>{u.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{u.email}</div>
                </div>
                {u.already_member
                  ? <span style={{ fontSize: '11px', color: '#10b981' }}>✅ Ya es miembro</span>
                  : (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {['vendedor', 'gerente', 'admin'].map(r => (
                        <button key={r} onClick={() => handleAdd(u.id, r)} style={{ padding: '5px 10px', borderRadius: '6px', border: 'none', background: '#7c3aed20', color: '#a78bfa', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>+ {r}</button>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
        {searched && !searching && searchResults.length === 0 && (
          <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>
            ❌ No se encontró ningún usuario registrado con ese email exacto.
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px' }}>👥 Miembros del equipo ({members.length})</div>
        {loading ? <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Cargando...</div>
          : members.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '8px', background: 'var(--bg-elevated)', marginBottom: '8px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#7c3aed30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '800', color: '#a78bfa', flexShrink: 0 }}>{(m.name?.[0] || '?').toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>{m.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{m.email}</div>
                <div style={{ marginTop: '6px' }}>
                  <input
                    autoComplete="off"
                    style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: '#fff', fontFamily: 'var(--font-body)', width: '160px' }}
                    placeholder="WhatsApp 573001234567"
                    defaultValue={m.whatsapp || ''}
                    onBlur={e => {
                      const val = e.target.value.trim();
                      if (val !== (m.whatsapp || '')) {
                        const api = require('../api/client').default;
                        api.patch(`/crm/tenants/mine/users/${m.user_id}`, { whatsapp: val })
                          .then(() => toast.success('WhatsApp guardado ✓'))
                          .catch(() => toast.error('Error guardando WhatsApp'));
                      }
                    }}
                  />
                </div>
              </div>
              <select value={m.role} onChange={e => handleChangeRole(m.user_id, e.target.value)}
                style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${ROLE_COLOR[m.role] || '#7c3aed'}40`, background: `${ROLE_COLOR[m.role] || '#7c3aed'}15`, color: ROLE_COLOR[m.role] || '#a78bfa', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <button onClick={() => handleRemove(m.user_id)} style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #ef444430', background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: '12px' }}>🗑</button>
            </div>
          ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// WHATSAPP TAB
// ════════════════════════════════════════════════════════════
function WhatsAppTab({ tenant, onSaved }) {
  const [form, setForm] = useState({
    whatsapp_phone_number_id: tenant?.whatsapp_phone_number_id || '',
    whatsapp_waba_id: tenant?.whatsapp_waba_id || '',
    whatsapp_token: tenant?.whatsapp_token || '',
    whatsapp_display_number: tenant?.whatsapp_display_number || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      await crmAPI.uploadLogo(file);
      toast.success('Logo actualizado ✓');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error subiendo el logo');
    }
    setUploadingLogo(false);
  };
  const isConfigured = tenant?.whatsapp_phone_number_id && tenant?.whatsapp_token;

  const handleSave = async () => {
    if (!form.whatsapp_phone_number_id || !form.whatsapp_token) return toast.error('Phone Number ID y Token son requeridos');
    setSaving(true);
    try {
      await crmAPI.updateTenant(form);
      toast.success('WhatsApp configurado ✓');
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ padding: '16px', borderRadius: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '16px' }}>
        {tenant?.logo_url
          ? <img src={tenant.logo_url} alt="Logo" style={{ width: '64px', height: '64px', objectFit: 'contain', borderRadius: '8px', background: '#fff' }} />
          : <div style={{ width: '64px', height: '64px', borderRadius: '8px', background: 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>🏢</div>
        }
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '4px' }}>Logo de la empresa</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>Se usa en las publicaciones automáticas en modo "Plantilla"</div>
          <label style={{ display: 'inline-block', padding: '6px 14px', borderRadius: '8px', background: '#7c3aed', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
            {uploadingLogo ? 'Subiendo...' : 'Subir logo'}
            <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogo} style={{ display: 'none' }} />
          </label>
        </div>
      </div>

      <div style={{ padding: '12px 16px', borderRadius: '10px', background: isConfigured ? '#10b98115' : '#f59e0b15', border: `1px solid ${isConfigured ? '#10b98140' : '#f59e0b40'}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '20px' }}>{isConfigured ? '✅' : '⚠️'}</span>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: isConfigured ? '#10b981' : '#f59e0b' }}>{isConfigured ? 'WhatsApp conectado' : 'WhatsApp no configurado'}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{isConfigured ? `Phone ID: ${tenant.whatsapp_phone_number_id}` : 'Configura tu número para activar el agente IA'}</div>
        </div>
      </div>

      <div style={{ ...cardStyle, background: 'var(--bg-elevated)' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '8px' }}>📋 Pasos para configurar</div>
        <ol style={{ fontSize: '12px', color: 'var(--text-muted)', paddingLeft: '16px', lineHeight: '2' }}>
          <li>Ve a <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" style={{ color: '#a78bfa' }}>developers.facebook.com</a> → crea app Business</li>
          <li>Agrega el producto "WhatsApp" a tu app</li>
          <li>En "Configuración API" copia el <strong style={{ color: '#fff' }}>Phone Number ID</strong></li>
          <li>Genera un <strong style={{ color: '#fff' }}>Token permanente</strong> (System User Token)</li>
          <li>En Meta, configura el webhook: <code style={{ background: '#7c3aed20', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>https://txmark.thitanx.com/whatsapp/webhook</code></li>
          <li>Token de verificación: <code style={{ background: '#7c3aed20', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>txmark_whatsapp_verify_2026</code></li>
          <li>Suscribe los eventos: <strong style={{ color: '#fff' }}>messages</strong></li>
        </ol>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div><label style={labelStyle}>Phone Number ID *</label><input autoComplete="off" style={inputStyle} value={form.whatsapp_phone_number_id} onChange={e => setForm(f => ({ ...f, whatsapp_phone_number_id: e.target.value }))} placeholder="Ej: 123456789012345" /></div>
        <div><label style={labelStyle}>WABA ID (opcional)</label><input autoComplete="off" style={inputStyle} value={form.whatsapp_waba_id} onChange={e => setForm(f => ({ ...f, whatsapp_waba_id: e.target.value }))} placeholder="Ej: 987654321098765" /></div>
        <div><label style={labelStyle}>Número de WhatsApp (formato internacional, sin +) *</label><input autoComplete="off" style={inputStyle} value={form.whatsapp_display_number} onChange={e => setForm(f => ({ ...f, whatsapp_display_number: e.target.value }))} placeholder="Ej: 573001234567" /><p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Se usa para generar el link directo de WhatsApp en las publicaciones automáticas.</p></div>
        <div>
          <label style={labelStyle}>Token de acceso permanente *</label>
          <input autoComplete="new-password" style={{ ...inputStyle, fontFamily: "monospace", fontSize: "11px" }} type="password" value={form.whatsapp_token} onChange={e => setForm(f => ({ ...f, whatsapp_token: e.target.value }))} placeholder="EAABxxx..." />
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>⚠️ Usa un token permanente, no el temporal de pruebas</div>
        </div>
      </div>

      <button disabled={saving} onClick={handleSave} style={{ padding: '12px', borderRadius: '8px', border: 'none', background: saving ? '#7c3aed90' : '#7c3aed', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
        {saving ? 'Guardando...' : '💾 Guardar configuración'}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN CRM PAGE
// ════════════════════════════════════════════════════════════
export default function CRMPage() {
  const [tab, setTab] = useState('dashboard');
  const [tenant, setTenant] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noTenant, setNoTenant] = useState(false);
  const [crmOpened, setCrmOpened] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes, sRes] = await Promise.all([
        crmAPI.getMyTenant(),
        crmAPI.getPipeline(),
        crmAPI.getSources(),
      ]);
      setTenant(tRes.data);
      setPipeline(pRes.data);
      setSources(sRes.data);
      const cRes = await crmAPI.listContacts({ limit: 200 }); setContacts(cRes.data?.items || cRes.data || []);
      setNoTenant(false);
    } catch (e) {
      if (e?.response?.status === 404) setNoTenant(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚙️</div>
        <div>Cargando CRM...</div>
      </div>
    </div>
  );

  if (noTenant) return <SetupWizard onDone={loadBase} />;

  const TABS = [
    { id: 'dashboard',   label: '📊 Dashboard' },
    { id: 'contacts',    label: '👥 Contactos' },
    { id: 'pipeline',    label: '📋 Pipeline' },
    { id: 'activities',  label: '⚡ Actividades' },
    { id: 'equipo',      label: '👤 Equipo' },
    { id: 'whatsapp',    label: '💬 WhatsApp' },
    { id: 'calendar',    label: '📅 Calendario' },
    { id: 'horarios',    label: '🕐 Horarios' },
  ];

  if (!crmOpened) {
    return (
      <div style={{ padding: '16px', maxWidth: '600px', margin: '60px auto' }}>
        <div
          onClick={() => setCrmOpened(true)}
          style={{ ...cardStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px', transition: 'transform 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <div style={{ width: '64px', height: '64px', borderRadius: '14px', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', overflow: 'hidden', flexShrink: 0 }}>
            {tenant?.logo_url ? <img src={tenant.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '🏢'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingName ? (
              <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: '8px' }}>
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '15px', fontWeight: '700' }}
                />
                <button
                  onClick={async () => {
                    if (!nameDraft.trim()) return toast.error('El nombre no puede estar vacío');
                    try { await crmAPI.updateTenant({ name: nameDraft.trim() }); toast.success('Nombre actualizado'); setEditingName(false); loadBase(); }
                    catch (e) { toast.error(e?.response?.data?.detail || 'Error al actualizar'); }
                  }}
                  style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                >💾</button>
                <button onClick={() => setEditingName(false)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer' }}>✕</button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: '18px', fontWeight: '800' }}>{tenant?.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Clic para entrar al CRM</div>
              </>
            )}
          </div>
          {!editingName && (
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
              <button
                onClick={() => { setNameDraft(tenant?.name || ''); setEditingName(true); }}
                title="Editar nombre"
                style={{ width: '34px', height: '34px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '14px' }}
              >✏️</button>
              <button
                onClick={async () => {
                  if (!window.confirm('¿Eliminar esta empresa? Esta acción no se puede deshacer.')) return;
                  try {
                    await crmAPI.deleteTenant();
                    toast.success('Empresa eliminada');
                    window.location.reload();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'No se pudo eliminar la empresa');
                  }
                }}
                title="Eliminar empresa (solo si está vacía)"
                style={{ width: '34px', height: '34px', borderRadius: '8px', border: '1px solid #ef444440', background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}
              >🗑️</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', maxWidth: '1200px', margin: '0 auto', paddingBottom: '80px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button onClick={() => setCrmOpened(false)} title="Volver" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>←</button>
        <div>
          <div style={{ fontSize: '22px', fontWeight: '800' }}>🏢 CRM — {tenant?.name}</div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>Gestión de contactos, deals y actividades</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--bg-elevated)', borderRadius: '10px', padding: '4px', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '8px 14px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', background: tab === t.id ? '#7c3aed' : 'transparent', color: tab === t.id ? '#fff' : 'var(--text-muted)' }}>{t.label}</button>
        ))}
      </div>
      {tab === 'dashboard'  && <DashboardTab />}
      {tab === 'contacts'   && <ContactsTab sources={sources} pipeline={pipeline} />}
      {tab === 'pipeline'   && <PipelineTab pipeline={pipeline} />}
      {tab === 'activities' && <ActivitiesTab />}
      {tab === 'equipo'     && <EquipoTab tenant={tenant} />}
      {tab === 'whatsapp'    && <WhatsAppTab tenant={tenant} onSaved={loadBase} />}
      {tab === 'calendar'   && <CalendarTab contacts={contacts} />}
      {tab === 'horarios'   && <BusinessHoursTab />}
    </div>
  );
}
